/**
 * Agent activity — Zod schemas (the buyer-facing analytics page).
 *
 * Field names match the backend (agentactivity handler) verbatim. The view is UNIT-AGNOSTIC:
 * every value/label rides the consequence dimension metadata (dimension key + display_name +
 * exponent + unit_code) — the frontend NEVER hard-codes a currency or a "$".
 */

import { z } from "zod";

export const activityPivotSchema = z.enum(["agent", "user"]);
export type ActivityPivot = z.infer<typeof activityPivotSchema>;

export const activityBucketSchema = z.enum(["hour", "day", "week", "month"]);
export type ActivityBucket = z.infer<typeof activityBucketSchema>;

// cap_status makes an empty cap band unambiguous — never let "no rows" read as "unlimited".
export const capStatusSchema = z.enum(["ok", "none", "unavailable", "not_applicable"]);
export type CapStatus = z.infer<typeof capStatusSchema>;

// reconciliation_status makes an absent reconciliation array unambiguous — never let "not computed"
// (user pivot / read fault / unwired reconciler) read as a clean "every action reconciled" verdict.
export const reconciliationCoverageSchema = z.enum(["evaluated", "not_applicable", "unavailable"]);
export type ReconciliationCoverage = z.infer<typeof reconciliationCoverageSchema>;

export const summarySchema = z.object({
  total: z.number().int().nonnegative(),
  permitted: z.number().int().nonnegative(),
  escalated: z.number().int().nonnegative(),
  denied: z.number().int().nonnegative(),
  quarantined: z.number().int().nonnegative(),
  first_at: z.string().optional(),
  last_at: z.string().optional(),
});
export type ActivitySummary = z.infer<typeof summarySchema>;

export const capSchema = z.object({
  dimension: z.string(),
  display_name: z.string(),
  exponent: z.number().int(),
  unit_code: z.string().optional(),
  used: z.number(),
  max: z.number(),
  pct: z.number(),
  window_kind: z.string(),
  window_seconds: z.number().optional(),
  scope: z.string(),
  // The exact ledger counter this gauge sums over — makes two same-(dimension,window,scope) gauges
  // on different counters (e.g. two delegation-scoped caps) distinct + a unique render key.
  subject: z.string(),
  // The running cumulative total over the window — the chart line climbing toward `max`.
  series: z.array(z.object({ at: z.string(), cumulative: z.number() })).optional(),
});
export type CapUtilization = z.infer<typeof capSchema>;
export type CapPoint = { at: string; cumulative: number };

// Display metadata for every dimension in the page (rows + caps), keyed by dimension key. The
// generic value renderer reads exponent + display_name from here — never a client-side unit map.
export const dimMetaSchema = z.object({
  display_name: z.string(),
  exponent: z.number().int(),
  unit_code: z.string().optional(),
});
export type DimMetaWire = z.infer<typeof dimMetaSchema>;

export const bucketSchema = z.object({
  at: z.string(), // RFC3339 UTC bucket start
  permitted: z.number().int().nonnegative(),
  escalated: z.number().int().nonnegative(),
  denied: z.number().int().nonnegative(),
  quarantined: z.number().int().nonnegative(),
});
export type ActivityBucketPoint = z.infer<typeof bucketSchema>;

export const measurementSchema = z.object({
  dimension: z.string(),
  quantity: z.number(),
});
export type Measurement = z.infer<typeof measurementSchema>;

export const counterpartySchema = z.object({
  counterparty: z.string(),
  count: z.number().int().nonnegative(),
  first_at: z.string(),
  last_at: z.string(),
  values: z.array(measurementSchema).optional(), // value moved per dimension
});
export type CounterpartyStat = z.infer<typeof counterpartySchema>;

// One row of the tenant-wide top-agents-by-cap-utilization leaderboard (the moat-first landing).
export const leaderboardSchema = z.object({
  agent_id: z.string(),
  agent_name: z.string().optional(),
  top_cap: capSchema,
});
export type LeaderboardEntry = z.infer<typeof leaderboardSchema>;

// The tenant-wide fleet cap histogram — the N-adaptive counterpart to the ranked leaderboard. Bands
// count every agent with an observable cumulative cap that the scan bucketed, so they ALWAYS sum to
// `scanned` (no phantom denominator). `complete` is true only when the scan exhausted the grant-holder
// set — then `scanned` is the whole fleet; when false it is a bounded sample the UI labels partial and
// never presents as the whole fleet.
export const capBandSchema = z.object({
  key: z.enum(["over", "crit", "high", "mid", "low"]),
  count: z.number(),
});
export const capDistributionSchema = z.object({
  bands: z.array(capBandSchema),
  scanned: z.number(),
  complete: z.boolean(),
  // When the rollup was last materialized (RFC3339); absent while a cold rollup is still computing.
  refreshed_at: z.string().optional(),
  // True ⇒ the first fleet rollup is still being computed (cold cache) — no counts to show yet.
  pending: z.boolean().optional(),
  // True ⇒ the rollup read/decode failed (distinct from pending — not "computing", won't fix by waiting).
  unavailable: z.boolean().optional(),
});
export type CapBand = z.infer<typeof capBandSchema>;
export type CapDistribution = z.infer<typeof capDistributionSchema>;

// Server-classified take-back recommendations (the worklist's take-back groups). Replaces the former
// client-side classification of the full standing-delegation list: fleet-counted, segment/subject-aware.
export const takeBackKindSchema = z.enum(["never", "overscoped", "idle", "recert"]);
export const takeBackSampleSchema = z.object({
  delegation_id: z.string(),
  agent_id: z.string(),
  agent_name: z.string().optional(),
  days: z.number(), // never: age · idle: days idle · recert: days-until-expiry (0 = today)
  pct: z.number(), // over-scoped: lifetime utilization %
  unit: z.string().optional(),
});
export const takeBackGroupSchema = z.object({
  kind: takeBackKindSchema,
  count: z.number(), // TRUE fleet count for this kind (samples is only the surfaced top-N)
  samples: z.array(takeBackSampleSchema),
});
export const takeBackSchema = z.object({
  groups: z.array(takeBackGroupSchema),
  // false ⇒ the scan truncated (too many delegations) or a util read failed → counts undercount.
  complete: z.boolean(),
  // when the materialized rollup was computed (RFC3339); absent for a live (focused/segmented) result.
  refreshed_at: z.string().optional(),
  // true ⇒ the rollup is cold and computing — the client fast-polls until it lands.
  pending: z.boolean().optional(),
  // true ⇒ the scan was throttled/errored and did NOT run (distinct from "ran and empty" and "pending").
  unavailable: z.boolean().optional(),
});
export type TakeBackKind = z.infer<typeof takeBackKindSchema>;
export type TakeBackSample = z.infer<typeof takeBackSampleSchema>;
export type TakeBackGroup = z.infer<typeof takeBackGroupSchema>;
export type TakeBack = z.infer<typeof takeBackSchema>;

// One caught executed-vs-authorized drift (the "we caught it" strip).
export const driftSchema = z.object({
  id: z.string(),
  agent_id: z.string(),
  // The registry kind of the drifting principal, snapshotted at detection (agent | workload_identity |
  // api_key). Lets the strip name a workload-identity overage as such — reconciliation is not agent-only.
  principal_kind: z.enum(["agent", "workload_identity", "api_key"]).default("agent"),
  grant_jti: z.string().optional(),
  dimension: z.string(),
  declared: z.number(),
  executed: z.number(),
  delta: z.number(),
  kind: z.string(), // over | off_book | tainted
  action_taken: z.string(), // observed | revoked
  detected_at: z.string(),
});
export type Drift = z.infer<typeof driftSchema>;

// One deterministic, rule-based "needs attention" signal (near/at a cumulative cap, a first-seen
// counterparty, or a cluster of hard refusals). Typed facts from the backend; the card composes the
// sentence. NOT a statistical anomaly — explainable and false-positive-free by construction.
export const attentionSchema = z.object({
  kind: z.enum(["cap_reached", "near_cap", "new_counterparty", "denial_cluster"]),
  severity: z.enum(["critical", "warning", "info"]),
  agent_id: z.string().optional(),
  agent_name: z.string().optional(),
  counterparty: z.string().optional(),
  dimension: z.string().optional(),
  pct: z.number().optional(),
  count: z.number().int().optional(),
});
export type AttentionSignal = z.infer<typeof attentionSchema>;

// A DISPLAY-ONLY resolved principal label (name-resolution sidecar). Two axis-separated maps carry
// these on the response — `agents` (keyed by agent_id) and `principals` (keyed by human_sub) — so the
// two disjoint id populations never collide and the overloaded "workload_identity" kind is never
// ambiguous (which map an id came from says which vocabulary its kind speaks). A resolved name is the
// CURRENT label; the signed evidence UUID remains the identifier of record (shown on the drill). An
// entry with deleted=true is an honest tombstone; an id ABSENT from a map is unresolved → the UI
// falls back to a short id.
export const principalLabelSchema = z.object({
  name: z.string().optional(),
  kind: z.string().optional(),
  deleted: z.boolean().optional(),
});
export type PrincipalLabel = z.infer<typeof principalLabelSchema>;

export const decisionRowSchema = z.object({
  jti: z.string(),
  seq: z.number().int(),
  decision: z.string(),
  agent_id: z.string(),
  human_sub: z.string(),
  counterparty: z.string().optional(),
  scope: z.string(),
  data_class: z.string(),
  consequence: z.array(measurementSchema).optional(),
  reason_codes: z.array(z.string()).optional(),
  occurred_at: z.string(),
});
export type DecisionRow = z.infer<typeof decisionRowSchema>;

// One dimension's "authority moved" split over the window: value that ran autonomously (permit), was
// routed to a human (step_up), or was blocked at the gate (deny+quarantine). Smallest-unit integers,
// rendered via the dimensions map — never summed across dimensions, never "prevented loss".
export const valueFunnelSchema = z.object({
  dimension: z.string(),
  autonomous: z.number(),
  routed_to_human: z.number(),
  blocked: z.number(),
});
export type ValueFunnelEntry = z.infer<typeof valueFunnelSchema>;

export const reasonStatSchema = z.object({
  reason_code: z.string(),
  count: z.number().int().nonnegative(),
});
export type ReasonStat = z.infer<typeof reasonStatSchema>;

// The prior equal-length window's totals, for "▲/▼ X% vs prior" deltas with a stated baseline.
export const comparisonSchema = z.object({
  prior_from: z.string(),
  prior_to: z.string(),
  prior: summarySchema,
});
export type Comparison = z.infer<typeof comparisonSchema>;

// One counterpart principal for a focused subject: an id on the OPPOSITE axis (an agent on the by-user
// view, a human on the by-agent view), its decision count, and last-seen. Full-window (server-side),
// so it is the complete set — not just the agents on the current page of the timeline.
export const counterpartActivitySchema = z.object({
  id: z.string(),
  count: z.number().int().nonnegative(),
  last_at: z.string(),
});
export type CounterpartActivity = z.infer<typeof counterpartActivitySchema>;

export const agentActivitySchema = z.object({
  pivot: activityPivotSchema,
  subject_id: z.string().optional(),
  window: z.object({ from: z.string(), to: z.string(), bucket: z.string() }),
  summary: summarySchema,
  cap_utilization: z.array(capSchema),
  cap_status: capStatusSchema,
  leaderboard: z.array(leaderboardSchema).optional(),
  cap_distribution: capDistributionSchema.optional(),
  take_back: takeBackSchema.optional(),
  dimensions: z.record(z.string(), dimMetaSchema).optional(),
  timeseries: z.array(bucketSchema),
  top_counterparties: z.array(counterpartySchema),
  reconciliation: z.array(driftSchema).optional(),
  // Disambiguates an absent `reconciliation` array (omitempty collapses "not computed" and "computed →
  // zero drift"): evaluated ⇒ the reconciler ran (empty ⇒ a genuine clean verdict); not_applicable ⇒
  // drift is agent-scoped (user pivot never runs it); unavailable ⇒ we couldn't evaluate. The UI must
  // NOT show a clean "every action reconciled" verdict unless this is "evaluated".
  reconciliation_status: reconciliationCoverageSchema.optional(),
  attention: z.array(attentionSchema).optional(),
  rows: z.array(decisionRowSchema),
  has_more: z.boolean(),
  next_before_seq: z.number().int().optional(),
  // Name-resolution sidecars (display-only). `agents` resolves agent_id → {name, kind}; `principals`
  // resolves human_sub → {name, kind, deleted}. Both optional — an older server or a resolve fault
  // omits them and the UI falls back to short ids.
  agents: z.record(z.string(), principalLabelSchema).optional(),
  principals: z.record(z.string(), principalLabelSchema).optional(),
  // Best-in-class analytics (full projection only; omitted on the compact/embedded projection).
  value_funnel: z.array(valueFunnelSchema).optional(),
  reason_rollup: z.array(reasonStatSchema).optional(),
  comparison: comparisonSchema.optional(),
  // For a focused subject: the distinct counterpart principals (agents that acted for a user; users an
  // agent acted for) over the full window. Absent on tenant-wide views.
  counterpart_rollup: z.array(counterpartActivitySchema).optional(),
});
export type AgentActivity = z.infer<typeof agentActivitySchema>;

// URL-driven page state. pivot + optional subject id + window (days) + bucket. All optional so the
// bare route lands on the tenant-wide agent overview.
// Segment dimensions — closed enums mirroring the agents-table CHECK constraints. Used both as URL
// search params (validated, .catch(undefined) so a stale/hand-edited value degrades to "no filter"
// rather than erroring the route) and as the source of the segment selector's options.
export const agentKindSchema = z.enum(["agent", "workload_identity", "api_key"]);
export const riskTierSchema = z.enum(["read_only", "low", "medium", "high"]);
export const autonomyTierSchema = z.enum(["supervised", "bounded", "autonomous"]);
export type AgentKind = z.infer<typeof agentKindSchema>;
export type RiskTier = z.infer<typeof riskTierSchema>;
export type AutonomyTier = z.infer<typeof autonomyTierSchema>;

export const activitySearchSchema = z.object({
  pivot: activityPivotSchema.optional().catch(undefined),
  id: z.string().optional().catch(undefined),
  // An out-of-set or malformed value degrades to undefined (→ the default window) rather than
  // hard-erroring the whole route — a stale bookmark or a hand-edited ?days should never break it.
  days: z.coerce.number().int().refine((n) => [7, 30, 90].includes(n)).optional().catch(undefined),
  bucket: activityBucketSchema.optional().catch(undefined),
  // Segment filter (re-scopes the whole page). Each degrades to undefined on an unknown value.
  kind: agentKindSchema.optional().catch(undefined),
  risk_tier: riskTierSchema.optional().catch(undefined),
  autonomy: autonomyTierSchema.optional().catch(undefined),
});
export type ActivitySearch = z.infer<typeof activitySearchSchema>;
